import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { SkeletonLine } from "../components/Skeleton";
import { useToast } from "../components/Toast";
import { IconPlus, IconSpinner } from "../components/Icons";
import type { ClientRecord, ClientType } from "../types";

const CLIENT_TYPE_OPTIONS: ClientType[] = ["owner", "lender", "investor", "other"];

function prettyType(t: ClientType): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export default function AdminClients() {
  const toast = useToast();
  const [clients, setClients] = useState<ClientRecord[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<ClientType>("lender");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.get<{ clients: ClientRecord[] }>("/api/admin/clients");
      setClients(data.clients);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load clients";
      setLoadErr(msg);
      toast.error("Clients failed to load", msg);
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setFormErr(null);
    setSubmitting(true);
    try {
      await api.post("/api/admin/clients", {
        name: name.trim(),
        clientType: type,
        notes: notes.trim() || null,
      });
      toast.success("Client created", `${name.trim()} is ready to invite users.`);
      setName("");
      setType("lender");
      setNotes("");
      setShowForm(false);
      await load();
    } catch (e) {
      setFormErr(e instanceof ApiError ? e.message : "Failed to create client");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="admin-content-header">
        <div>
          <div className="admin-content-title">Clients</div>
          <div className="admin-content-sub">
            Organizations we serve &mdash; owners, lenders, investors.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Close" : <><IconPlus size={14} />Add client</>}
        </button>
      </div>

      {loadErr && <div className="alert alert-error" role="alert">{loadErr}</div>}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="card"
          style={{ marginBottom: "var(--space-5)", padding: "var(--space-4)" }}
          noValidate
        >
          <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-4)" }}>
            New client
          </h2>

          {formErr && <div className="alert alert-error" role="alert">{formErr}</div>}

          <div className="form-group">
            <label htmlFor="client-name">Name</label>
            <input
              id="client-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="client-type">Type</label>
            <select
              id="client-type"
              value={type}
              onChange={(e) => setType(e.target.value as ClientType)}
            >
              {CLIENT_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{prettyType(t)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="client-notes">Notes (optional)</label>
            <textarea
              id="client-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting && <IconSpinner size={14} />}
              {submitting ? "Creating…" : "Create client"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {!clients ? (
        <LoadingTable />
      ) : clients.length === 0 ? (
        <EmptyState
          heading="No clients yet"
          body="Add your first organization to start tracking property records and inviting their team."
          action={
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              <IconPlus size={14} />Add your first client
            </button>
          }
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Name</th>
              <th style={{ width: "14%" }}>Type</th>
              <th style={{ width: "20%" }}>Slug</th>
              <th style={{ width: "10%" }}>Users</th>
              <th style={{ width: "14%" }}>Created</th>
              <th style={{ width: "12%" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="row-link">
                <td>
                  <Link
                    to={`/admin/clients/${c.id}`}
                    style={{
                      textDecoration: "none",
                      color: "var(--ink)",
                      fontWeight: 500,
                    }}
                  >
                    {c.name}
                  </Link>
                </td>
                <td>{prettyType(c.clientType)}</td>
                <td>
                  <code
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--text-sm)",
                      color: "var(--muted)",
                    }}
                  >
                    /c/{c.slug}
                  </code>
                </td>
                <td className="num">{c.userCount ?? 0}</td>
                <td className="text-muted num" style={{ whiteSpace: "nowrap" }}>
                  {new Date(c.createdAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </td>
                <td>
                  <span className={`badge badge-${c.active ? "active" : "inactive"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LoadingTable() {
  return (
    <table className="data-table" aria-busy="true">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Slug</th>
          <th>Users</th>
          <th>Created</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {[0, 1, 2].map((i) => (
          <tr key={i}>
            <td><SkeletonLine width="70%" /></td>
            <td><SkeletonLine width="50%" /></td>
            <td><SkeletonLine width="60%" /></td>
            <td><SkeletonLine width="30%" /></td>
            <td><SkeletonLine width="55%" /></td>
            <td><SkeletonLine width="45%" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
